// src/layout/MainLayout.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import Navbar from '../components/Navbar';
import Header from '../components/Header';

const MainLayout = ({ children, userData }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentPageTitle, setCurrentPageTitle] = useState('Dashboard');
  const isMobile = useMediaQuery("(max-width:800px)");
  const location = useLocation();

  // Set page title based on current route
  useEffect(() => {
    const routeTitles = {
      '/dashboard': 'Dashboard',
      '/leads': 'Leads',
      '/calendar': 'Calendar',
      '/all-leads': 'All Leads',
      '/add-lead': 'Add New Lead',
      '/notifications': 'Notifications',
      '/reports': 'Reports',
      '/settings': 'Settings',
      '/profile': 'Profile',
      '/manage-users': 'Manage Users',
      '/lead/': 'Lead Details',
      '/add-company': 'Companies',
      '/meetings': 'Meetings',
      '/quotations': 'Quotations',
      '/team-collaboration': 'Team Collaboration',
    };

    const currentPath = location.pathname;
    let pageTitle = 'Dashboard';
    
    if (routeTitles[currentPath]) {
      pageTitle = routeTitles[currentPath];
    } else {
      for (const [route, title] of Object.entries(routeTitles)) {
        if (currentPath.startsWith(route) && route !== '/') {
          pageTitle = title;
          break;
        }
      }
    }

    setCurrentPageTitle(pageTitle);
  }, [location.pathname]);

  // Auto-manage sidebar based on screen size
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, sidebarOpen]);

  // ✅ FIXED: Proper toggle function that works for both mobile and desktop
  const handleSidebarToggle = (newState) => {
    // If newState is provided, use it (from Navbar)
    // Otherwise toggle the current state (from Header)
    if (newState !== undefined) {
      setSidebarOpen(newState);
    } else {
      setSidebarOpen(prev => !prev);
    }
  };

  const drawerWidth = sidebarOpen ? 240 : 68;

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* ✅ Pass the current state and proper toggle function */}
      <Navbar open={sidebarOpen} onToggle={handleSidebarToggle} />
      <Header 
        open={sidebarOpen} 
        onToggle={handleSidebarToggle} 
        user={userData}
        pageTitle={currentPageTitle}
      />
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
          marginLeft: 0,
          paddingTop: { xs: '56px', sm: '64px', md: '72px' },
          transition: 'all 0.3s ease-in-out',
          minHeight: '100vh',
          overflow: 'auto',
        }}
      >
        {/* Content Container */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            padding: { xs: 1, sm: 2, md: 3 },
            boxSizing: 'border-box'
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;